import React, { useState, useEffect } from 'react';
import { Users, Activity, Settings, TrendingUp, Search, Trash2, Shield, UserCheck, AlertCircle, FileText } from 'lucide-react';

export default function AdminDashboard({ themeStyles, API_URL, token }) {
    const [activeSubTab, setActiveSubTab] = useState('overview');
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // User Management State
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        fetchStats();
    }, []);

    useEffect(() => {
        if (activeSubTab === 'users') fetchUsers();
        if (activeSubTab === 'logs') fetchLogs();
    }, [activeSubTab, page]);

    const fetchStats = async () => {
        try {
            const res = await fetch(`${API_URL}/admin/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
            const data = await res.json();
            if (data.stats) setStats(data.stats);
        } catch (err) { console.error(err); }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/admin/users?page=${page}&limit=20&search=${searchTerm}`, { headers: { 'Authorization': `Bearer ${token}` } });
            const data = await res.json();
            if (data.users) {
                setUsers(data.users);
                setTotalPages(data.pagination.pages);
            }
        } catch (err) { setError(err.message); }
        setLoading(false);
    };

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/admin/activity-logs`, { headers: { 'Authorization': `Bearer ${token}` } });
            const data = await res.json();
            if (data.logs) setLogs(data.logs);
        } catch (err) { setError(err.message); }
        setLoading(false);
    };

    const handlePromote = async (userId, currentRole) => {
        if (!window.confirm('Voulez-vous vraiment changer le rôle de cet utilisateur ?')) return;
        const newRole = currentRole === 'admin' ? 'user' : 'admin';
        try {
            await fetch(`${API_URL}/admin/users/${userId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ role: newRole })
            });
            fetchUsers();
        } catch (err) { alert('Erreur: ' + err.message); }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('ATTENTION: Suppression définitive. Continuer ?')) return;
        try {
            await fetch(`${API_URL}/admin/users/${userId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchUsers();
        } catch (err) { alert('Erreur: ' + err.message); }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchUsers();
    };

    // UI Components
    const StatCard = ({ icon: Icon, label, value, color }) => (
        <div className={`p-6 rounded-2xl border ${themeStyles.card} flex items-center gap-4`}>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
                <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
                <p className={`text-sm ${themeStyles.textSecondary}`}>{label}</p>
                <p className={`text-2xl font-bold ${themeStyles.textPrimary}`}>{value}</p>
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Sub-Navigation */}
            <div className="flex gap-2 border-b border-gray-700/50 pb-2 overflow-x-auto">
                {[
                    { id: 'overview', label: "Vue d'ensemble", icon: TrendingUp },
                    { id: 'users', label: 'Utilisateurs', icon: Users },
                    { id: 'logs', label: 'Logs Système', icon: FileText },
                    { id: 'settings', label: 'Configuration', icon: Settings },
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveSubTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeSubTab === tab.id ? themeStyles.button.primary : `hover:bg-gray-800 ${themeStyles.textSecondary}`
                            }`}
                    >
                        <tab.icon className="w-4 h-4" /> {tab.label}
                    </button>
                ))}
            </div>

            {/* OVERVIEW */}
            {activeSubTab === 'overview' && stats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-in fade-in">
                    <StatCard icon={Users} label="Utilisateurs Totaux" value={stats.users} color="bg-blue-500" />
                    <StatCard icon={UserCheck} label="Abonnements Actifs" value={stats.active_subs} color="bg-emerald-500" />
                    <StatCard icon={Activity} label="Revenus (Simulés)" value={`${stats.revenue / 100}€`} color="bg-purple-500" />
                </div>
            )}

            {/* USERS */}
            {activeSubTab === 'users' && (
                <div className={`p-4 sm:p-6 rounded-2xl border ${themeStyles.card} animate-in fade-in`}>
                    <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
                        <h3 className={`text-lg font-bold ${themeStyles.textPrimary}`}>Gestion des Utilisateurs</h3>
                        <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto">
                            <input
                                type="text"
                                placeholder="Rechercher..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="bg-slate-900 border border-slate-700 text-white px-3 py-2 rounded-lg text-sm flex-1 min-w-0 sm:flex-none"
                            />
                            <button type="submit" className="bg-slate-800 p-2 rounded-lg hover:bg-slate-700"><Search className="w-4 h-4 text-white" /></button>
                        </form>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left min-w-[680px]">
                            <thead className="text-xs uppercase bg-slate-800/50 text-slate-400">
                                <tr>
                                    <th className="px-4 py-3">Email</th>
                                    <th className="px-4 py-3">Entreprise</th>
                                    <th className="px-4 py-3">Rôle</th>
                                    <th className="px-4 py-3">Abonnement</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800">
                                {users.map(u => (
                                    <tr key={u.id} className="hover:bg-slate-800/30 transition-colors">
                                        <td className={`px-4 py-3 font-medium ${themeStyles.textPrimary}`}>{u.email}</td>
                                        <td className="px-4 py-3 text-slate-400">{u.company_name}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${u.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-slate-700 text-slate-300'}`}>
                                                {u.role}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs ${u.subscription_status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                                {u.subscription_plan || 'free'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right flex justify-end gap-2">
                                            <button onClick={() => handlePromote(u.id, u.role)} title={u.role === 'admin' ? 'Rétrograder' : 'Promouvoir Admin'} className="p-1.5 hover:bg-purple-500/20 text-purple-400 rounded transition-colors">
                                                <Shield className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => handleDelete(u.id)} title="Supprimer" className="p-1.5 hover:bg-red-500/20 text-red-400 rounded transition-colors">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:justify-between items-center mt-4">
                        <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="text-sm text-slate-400 disabled:opacity-50">Précédent</button>
                        <span className="text-sm text-slate-500">Page {page} / {totalPages}</span>
                        <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="text-sm text-slate-400 disabled:opacity-50">Suivant</button>
                    </div>
                </div>
            )}

            {/* LOGS */}
            {activeSubTab === 'logs' && (
                <div className={`p-4 sm:p-6 rounded-2xl border ${themeStyles.card} animate-in fade-in`}>
                    <h3 className={`text-lg font-bold ${themeStyles.textPrimary} mb-4`}>Logs d'activité du système</h3>
                    <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                        <table className="w-full text-sm text-left min-w-[680px]">
                            <thead className="text-xs uppercase bg-slate-800/50 text-slate-400 sticky top-0 backdrop-blur-md">
                                <tr>
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Utilisateur</th>
                                    <th className="px-4 py-3">Action</th>
                                    <th className="px-4 py-3">Cible</th>
                                    <th className="px-4 py-3">Détails</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800">
                                {logs.map(log => (
                                    <tr key={log.id} className="hover:bg-slate-800/30">
                                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                                        <td className={`px-4 py-3 font-medium ${themeStyles.textPrimary}`}>{log.email}</td>
                                        <td className="px-4 py-3"><span className="bg-blue-900/40 text-blue-300 px-2 py-0.5 rounded text-xs border border-blue-800">{log.action}</span></td>
                                        <td className="px-4 py-3 text-slate-400">{log.resource}</td>
                                        <td className="px-4 py-3 text-slate-500 font-mono text-xs truncate max-w-xs" title={JSON.stringify(log.details)}>{JSON.stringify(log.details)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* SETTINGS */}
            {activeSubTab === 'settings' && (
                <div className={`p-4 sm:p-8 rounded-2xl border ${themeStyles.card} flex flex-col items-center justify-center min-h-[300px] text-center`}>
                    <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                        <Settings className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className={`text-xl font-bold ${themeStyles.textPrimary}`}>Paramètres Système</h3>
                    <p className="text-slate-500 mt-2 max-w-md">La configuration des modèles IA et des clés API sera disponible ici prochainement.</p>
                </div>
            )}
        </div>
    );
}
